import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, TextField, InputAdornment, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TablePagination, Chip, Card, CardContent,
  FormControl, InputLabel, Select, MenuItem, CircularProgress, Alert, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Tooltip, Collapse,
  ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Search as SearchIcon, Refresh as RefreshIcon, Security as SecurityIcon,
  Error as ErrorIcon, Warning as WarningIcon, Info as InfoIcon,
  LocationOn as LocationIcon, CarCrash as AccidentIcon, Phone as PhoneIcon,
  Shield as ShieldIcon, BugReport as BugIcon, ExpandMore, ExpandLess,
  Close as CloseIcon,
} from '@mui/icons-material';
import { apiService } from '../../services/api';
import AuditGroupRow from '../../components/admin/AuditGroupRow';
import DetalleEventoWorker from './DetalleEventoWorker';
import { Persona, Equipo } from '../../components/admin/IdentidadAuditoria';

const LEVEL_CONFIG: Record<string, { color: any; icon: React.ReactNode; label: string }> = {
  info: { color: 'info', icon: <InfoIcon fontSize="small" />, label: 'Info' },
  warn: { color: 'warning', icon: <WarningIcon fontSize="small" />, label: 'Warn' },
  security: { color: 'error', icon: <SecurityIcon fontSize="small" />, label: 'Security' },
  error: { color: 'error', icon: <ErrorIcon fontSize="small" />, label: 'Error' },
};

const CATEGORY_CONFIG: Record<string, { icon: React.ReactNode; label: string }> = {
  safezone: { icon: <LocationIcon fontSize="small" />, label: 'SafeZone' },
  accident: { icon: <AccidentIcon fontSize="small" />, label: 'Accidente' },
  sinch: { icon: <PhoneIcon fontSize="small" />, label: 'Sinch' },
  security: { icon: <ShieldIcon fontSize="small" />, label: 'Seguridad' },
  error: { icon: <BugIcon fontSize="small" />, label: 'Error' },
};

function timeAgo(date: string | Date): string {
  const now = new Date().getTime();
  const then = new Date(date).getTime();
  const diff = Math.floor((now - then) / 1000);
  if (diff < 60) return `hace ${diff}s`;
  if (diff < 3600) return `hace ${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `hace ${Math.floor(diff / 3600)}h`;
  return `hace ${Math.floor(diff / 86400)}d`;
}

function formatDate(date: string | Date): string {
  return new Date(date).toLocaleString('es-DO', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
}

const AuditLogsPage: React.FC = () => {
  const [logs, setLogs] = useState<any[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [total, setTotal] = useState(0);
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [detailDialog, setDetailDialog] = useState<any>(null);
  // Grouped is the default: one row per incident instead of one row per
  // internal worker step. The flat view stays a click away for debugging.
  const [viewMode, setViewMode] = useState<'grouped' | 'flat'>('grouped');
  const [groups, setGroups] = useState<any[]>([]);

  // Filters
  const [level, setLevel] = useState('');
  const [category, setCategory] = useState('');
  const [search, setSearch] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: any = { page: page + 1, limit: rowsPerPage };
      if (level) params.level = level;
      if (category) params.category = category;
      if (search) params.search = search;
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      if (viewMode === 'grouped') {
        const res = await apiService.getAuditLogsGrouped(params);
        setGroups(res.data || []);
        setTotal(res.pagination?.total || 0);
      } else {
        const res = await apiService.getAuditLogs(params);
        setLogs(res.data || []);
        setTotal(res.pagination?.total || 0);
      }
    } catch (e: any) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, level, category, search, startDate, endDate, viewMode]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await apiService.getAuditStats();
      setStats(res.data);
    } catch {}
  }, []);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);
  useEffect(() => { fetchStats(); }, [fetchStats]);

  const handleRefresh = () => { fetchLogs(); fetchStats(); };

  const getRowBg = (log: any) => {
    if (log.level === 'security') return 'rgba(211, 47, 47, 0.04)';
    if (log.level === 'error') return 'rgba(255, 152, 0, 0.04)';
    return undefined;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>Auditoría del Worker</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ToggleButtonGroup size="small" exclusive value={viewMode}
            onChange={(_, v) => { if (v) { setViewMode(v); setPage(0); setExpandedRow(null); } }}>
            <ToggleButton value="grouped">Agrupado</ToggleButton>
            <ToggleButton value="flat">Detallado</ToggleButton>
          </ToggleButtonGroup>
          <Tooltip title="Actualizar">
            <IconButton onClick={handleRefresh} color="primary"><RefreshIcon /></IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Stats Cards */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            { label: 'Hoy', value: stats.todayCount || 0, color: 'primary.main', icon: <InfoIcon /> },
            { label: 'Seguridad', value: stats.byLevel?.security || 0, color: 'error.main', icon: <SecurityIcon /> },
            { label: 'Errores', value: stats.byLevel?.error || 0, color: 'warning.main', icon: <ErrorIcon /> },
            { label: 'SafeZone', value: stats.byCategory?.safezone || 0, color: 'info.main', icon: <LocationIcon /> },
          ].map((s, i) => (
            <Grid size={{ xs: 6, md: 3 }} key={i}>
              <Card>
                <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2, '&:last-child': { pb: 2 } }}>
                  <Box sx={{ backgroundColor: s.color, color: 'white', borderRadius: 2, p: 1.5, display: 'flex' }}>{s.icon}</Box>
                  <Box>
                    <Typography variant="h5" fontWeight={600}>{s.value}</Typography>
                    <Typography variant="body2" color="text.secondary">{s.label}</Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Nivel</InputLabel>
              <Select value={level} label="Nivel" onChange={(e) => { setLevel(e.target.value); setPage(0); }}>
                <MenuItem value="">Todos</MenuItem>
                {Object.entries(LEVEL_CONFIG).map(([k, v]) => (
                  <MenuItem key={k} value={k}>{v.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Categoría</InputLabel>
              <Select value={category} label="Categoría" onChange={(e) => { setCategory(e.target.value); setPage(0); }}>
                <MenuItem value="">Todas</MenuItem>
                {Object.entries(CATEGORY_CONFIG).map(([k, v]) => (
                  <MenuItem key={k} value={k}>{v.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <TextField fullWidth size="small" type="date" label="Desde" value={startDate}
              onChange={(e) => { setStartDate(e.target.value); setPage(0); }}
              InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <TextField fullWidth size="small" type="date" label="Hasta" value={endDate}
              onChange={(e) => { setEndDate(e.target.value); setPage(0); }}
              InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField fullWidth size="small" placeholder="Buscar por acción, userId, deviceId..."
              value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
            />
          </Grid>
        </Grid>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Table */}
      <TableContainer component={Paper}>
        {loading && <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>}
        {!loading && (
          <>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width={40}></TableCell>
                  <TableCell>{viewMode === 'grouped' ? 'Último paso' : 'Timestamp'}</TableCell>
                  <TableCell>Nivel</TableCell>
                  <TableCell>Categoría</TableCell>
                  <TableCell>{viewMode === 'grouped' ? 'Incidente' : 'Acción'}</TableCell>
                  <TableCell>Usuario</TableCell>
                  <TableCell>Equipo</TableCell>
                  <TableCell align={viewMode === 'grouped' ? 'center' : 'left'}>
                    {viewMode === 'grouped' ? 'Pasos' : 'Detalles'}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {viewMode === 'grouped' && groups.map((g: any) => (
                  <AuditGroupRow
                    key={g.key}
                    group={g}
                    expanded={expandedRow === g.key}
                    onToggle={() => setExpandedRow(expandedRow === g.key ? null : g.key)}
                    onShowRaw={(entry) => setDetailDialog(entry)}
                  />
                ))}
                {viewMode === 'grouped' && groups.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No se encontraron incidentes</Typography>
                    </TableCell>
                  </TableRow>
                )}
                {viewMode === 'flat' && logs.map((log) => (
                  <React.Fragment key={log._id}>
                    <TableRow hover sx={{ backgroundColor: getRowBg(log), cursor: 'pointer' }}
                      onClick={() => setExpandedRow(expandedRow === log._id ? null : log._id)}>
                      <TableCell>
                        <IconButton size="small">
                          {expandedRow === log._id ? <ExpandLess /> : <ExpandMore />}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={formatDate(log.timestamp)}>
                          <Typography variant="body2" noWrap>{timeAgo(log.timestamp)}</Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={LEVEL_CONFIG[log.level]?.label || log.level}
                          color={LEVEL_CONFIG[log.level]?.color || 'default'}
                          icon={LEVEL_CONFIG[log.level]?.icon as any} />
                      </TableCell>
                      <TableCell>
                        <Chip size="small" variant="outlined"
                          label={CATEGORY_CONFIG[log.category]?.label || log.category}
                          icon={CATEGORY_CONFIG[log.category]?.icon as any} />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <Typography variant="body2" fontFamily="monospace" fontSize="0.8rem">{log.action}</Typography>
                          {/* Los eventos de seguridad llegan agrupados por dia: una fila con su
                              contador, en vez de cincuenta lineas identicas. */}
                          {log.veces > 1 && (
                            <Tooltip title={`${log.veces} veces este dia${log.sourceIps?.length ? `, desde ${log.sourceIps.length} IP(s)` : ''}`}>
                              <Chip size="small" color="default" label={`×${log.veces}`} sx={{ height: 18, fontSize: '0.7rem' }} />
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Persona id={log.userId} nombre={log.userName} correo={log.userEmail} />
                      </TableCell>
                      <TableCell>
                        <Equipo id={log.deviceId} nombre={log.deviceName} imei={log.deviceImei} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                          {log.details ? JSON.stringify(log.details).substring(0, 60) + '...' : '-'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={8} sx={{ py: 0, borderBottom: expandedRow === log._id ? undefined : 'none' }}>
                        <Collapse in={expandedRow === log._id} timeout="auto" unmountOnExit>
                          <Box sx={{ p: 2, backgroundColor: 'grey.50', borderRadius: 1, my: 1 }}>
                            {/* Primero lo que pasó, en cristiano; el JSON queda debajo. */}
                            <DetalleEventoWorker log={log} />
                            <Grid container spacing={2}>
                              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                <Typography variant="caption" color="text.secondary">Timestamp completo</Typography>
                                <Typography variant="body2">{formatDate(log.timestamp)}</Typography>
                              </Grid>
                              <Grid size={{ xs: 6, sm: 3, md: 2 }}>
                                <Typography variant="caption" color="text.secondary">Event ID</Typography>
                                <Typography variant="body2" fontFamily="monospace">{log.eventId ?? '-'}</Typography>
                              </Grid>
                              <Grid size={{ xs: 6, sm: 3, md: 2 }}>
                                <Typography variant="caption" color="text.secondary">Worker Version</Typography>
                                <Typography variant="body2">{log.workerVersion ?? '-'}</Typography>
                              </Grid>
                              <Grid size={{ xs: 6, sm: 3, md: 2 }}>
                                <Typography variant="caption" color="text.secondary">CF-Ray</Typography>
                                <Typography variant="body2" fontFamily="monospace" fontSize="0.75rem">{log.cfRay ?? '-'}</Typography>
                              </Grid>
                              <Grid size={{ xs: 6, sm: 3, md: 3 }}>
                                <Typography variant="caption" color="text.secondary">Source IP</Typography>
                                <Typography variant="body2" fontFamily="monospace">{log.sourceIp ?? '-'}</Typography>
                              </Grid>
                              <Grid size={{ xs: 12 }}>
                                <Typography variant="caption" color="text.secondary">JSON completo</Typography>
                                <Paper variant="outlined" sx={{ p: 1.5, mt: 0.5, maxHeight: 300, overflow: 'auto' }}>
                                  <pre style={{ margin: 0, fontSize: '0.8rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                    {JSON.stringify(log.details, null, 2)}
                                  </pre>
                                </Paper>
                              </Grid>
                            </Grid>
                            <Box sx={{ mt: 1, textAlign: 'right' }}>
                              <Button size="small" onClick={() => setDetailDialog(log)}>Ver completo</Button>
                            </Box>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
                {viewMode === 'flat' && logs.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No se encontraron registros</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div" count={total} page={page} rowsPerPage={rowsPerPage}
              onPageChange={(_, p) => setPage(p)}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
              rowsPerPageOptions={[10, 25, 50, 100]}
              labelRowsPerPage={viewMode === 'grouped' ? 'Incidentes por página' : 'Filas por página'}
            />
          </>
        )}
      </TableContainer>

      {/* Full Detail Dialog */}
      <Dialog open={!!detailDialog} onClose={() => setDetailDialog(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Detalle del Registro
          <IconButton onClick={() => setDetailDialog(null)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {detailDialog && (
            <pre style={{ margin: 0, fontSize: '0.85rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {JSON.stringify(detailDialog, null, 2)}
            </pre>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialog(null)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AuditLogsPage;
